import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { RefreshCw, CheckCircle, AlertCircle, Database, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

interface AggregatesStatus {
  summary: {
    totalCompanies: number;
    companiesWithAggregates: number;
    companiesNeedingRebuild: number;
    companiesWithNoData: number;
    totalAggregateDocuments: number;
    totalCommissionVolume: number;
  };
  companies: Array<{
    companyId: string;
    companyName: string;
    hasCommissionData: boolean;
    aggregateCount: number;
    totalCommission: number;
    months: string[];
    status: string;
  }>;
}

interface ComparisonResult {
  summary: {
    totalCompanies: number;
    companiesMatching: number;
    companiesMismatched: number;
    platformSourceTotal: number;
    platformAggregateTotal: number;
    platformDifference: number;
    percentageDifference: number;
  };
  companies: Array<{
    companyId: string;
    companyName: string;
    status: string;
    sourceData: {
      upfront: number;
      trail: number;
      total: number;
      recordCount: number;
    };
    aggregates: {
      upfront: number;
      trail: number;
      total: number;
      recordCount: number;
      documentCount: number;
    };
    differences: {
      upfront: number;
      trail: number;
      total: number;
      recordCount: number;
    };
    matches: {
      upfront: boolean;
      trail: boolean;
      total: boolean;
      recordCount: boolean;
    };
  }>;
  diagnosis: string;
}

interface PaymentTypeAnalysis {
  summary: {
    totalUniquePaymentTypes: number;
    countedTypes: number;
    uncountedTypes: number;
    countedTotal: number;
    uncountedTotal: number;
    uncountedRecordCount: number;
  };
  uncountedTypes: Array<{
    paymentType: string;
    recordCount: number;
    totalAmount: number;
    isUpfront: boolean;
    isTrail: boolean;
    isCounted: boolean;
    appearsInCompanies: string[];
  }>;
  allTypes: Array<{
    paymentType: string;
    recordCount: number;
    totalAmount: number;
    isUpfront: boolean;
    isTrail: boolean;
    isCounted: boolean;
    appearsInCompanies: string[];
  }>;
}

export const AggregatesDiagnosticsTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [status, setStatus] = useState<AggregatesStatus | null>(null);
  const [comparison, setComparison] = useState<ComparisonResult | null>(null);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const checkAggregatesStatus = httpsCallable(functions, 'checkAggregatesStatus');
      const result = await checkAggregatesStatus();
      setStatus(result.data as AggregatesStatus);
      toast.success('Status check complete');
    } catch (err: any) {
      console.error('Error checking aggregates status:', err);
      setError(err.message || 'Failed to check aggregates status');
      toast.error('Failed to check status');
    } finally {
      setLoading(false);
    }
  };

  const runComparison = async () => {
    setComparing(true);
    setError(null);

    try {
      toast.loading('Comparing source data vs aggregates... This will take 2-3 minutes', { duration: 200000 });
      const compareAggregatesAccuracy = httpsCallable(functions, 'compareAggregatesAccuracy', {
        timeout: 600000 // 10 minutes
      });
      const result = await compareAggregatesAccuracy();
      setComparison(result.data as ComparisonResult);
      toast.dismiss();
      toast.success('Comparison complete');
    } catch (err: any) {
      console.error('Error comparing aggregates:', err);
      const errorMsg = err.message || 'Failed to compare aggregates';
      toast.dismiss();

      // Check if it's a timeout - the function might still be running
      if (errorMsg.includes('deadline-exceeded') || errorMsg.includes('timeout')) {
        toast.error('Request timed out - check Cloud Function logs for results', { duration: 8000 });
        setError('Request timed out. The function may still be running. Check Cloud Function logs for "compareAggregatesAccuracy" to see results.');
      } else {
        toast.error('Failed to run comparison');
        setError(errorMsg);
      }
    } finally {
      setComparing(false);
    }
  };

  const analyzePaymentTypesHandler = async () => {
    setAnalyzing(true);
    setError(null);

    try {
      toast.loading('Analyzing payment types... This will take 2-3 minutes', { duration: 200000 });
      const analyzePaymentTypes = httpsCallable(functions, 'analyzePaymentTypes', {
        timeout: 600000 // 10 minutes
      });
      const result = await analyzePaymentTypes();
      setPaymentTypes(result.data as PaymentTypeAnalysis);
      toast.dismiss();
      toast.success('Payment type analysis complete');
    } catch (err: any) {
      console.error('Error analyzing payment types:', err);
      const errorMsg = err.message || 'Failed to analyze payment types';
      toast.dismiss();

      if (errorMsg.includes('deadline-exceeded') || errorMsg.includes('timeout')) {
        toast.error('Request timed out - check Cloud Function logs for results', { duration: 8000 });
        setError('Request timed out. The function may still be running. Check Cloud Function logs for "analyzePaymentTypes" to see results.');
      } else {
        toast.error('Failed to analyze payment types');
        setError(errorMsg);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const getStatusColor = (statusText: string) => {
    if (statusText.includes('✅')) return 'border-green-500';
    if (statusText.includes('⚠️')) return 'border-orange-500';
    return 'border-gray-500';
  };

  const getStatusBadgeColor = (statusText: string) => {
    if (statusText.includes('✅')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (statusText.includes('⚠️')) return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Aggregates Diagnostics</h2>
            <p className="text-slate-400 text-sm max-w-2xl">
              Check which companies have monthly_aggregates populated and identify any that need rebuilding.
              Monthly aggregates are pre-calculated summaries that power commission metrics in the Analytics dashboard.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={checkStatus}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Checking...' : 'Check Status'}
            </button>
            <button
              onClick={runComparison}
              disabled={comparing}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-medium hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <Database className={`w-4 h-4 ${comparing ? 'animate-pulse' : ''}`} />
              {comparing ? 'Comparing...' : 'Compare Accuracy'}
            </button>
            <button
              onClick={analyzePaymentTypesHandler}
              disabled={analyzing}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <FileText className={`w-4 h-4 ${analyzing ? 'animate-pulse' : ''}`} />
              {analyzing ? 'Analyzing...' : 'Analyze Payment Types'}
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-400 font-semibold mb-1">Error</h3>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {status && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-2">
                <Database className="w-5 h-5 text-blue-400" />
                <h3 className="text-slate-400 text-sm font-medium">Total Companies</h3>
              </div>
              <p className="text-3xl font-bold text-white">{status.summary.totalCompanies}</p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-green-500/30">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <h3 className="text-slate-400 text-sm font-medium">With Aggregates</h3>
              </div>
              <p className="text-3xl font-bold text-green-400">{status.summary.companiesWithAggregates}</p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-orange-500/30">
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="w-5 h-5 text-orange-400" />
                <h3 className="text-slate-400 text-sm font-medium">Need Rebuild</h3>
              </div>
              <p className="text-3xl font-bold text-orange-400">{status.summary.companiesNeedingRebuild}</p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-2">
                <Database className="w-5 h-5 text-gray-400" />
                <h3 className="text-slate-400 text-sm font-medium">No Data Yet</h3>
              </div>
              <p className="text-3xl font-bold text-gray-400">{status.summary.companiesWithNoData}</p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-2">
                <Database className="w-5 h-5 text-purple-400" />
                <h3 className="text-slate-400 text-sm font-medium">Total Aggregate Docs</h3>
              </div>
              <p className="text-3xl font-bold text-purple-400">{status.summary.totalAggregateDocuments}</p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-2">
                <Database className="w-5 h-5 text-emerald-400" />
                <h3 className="text-slate-400 text-sm font-medium">Total Commission Volume</h3>
              </div>
              <p className="text-3xl font-bold text-emerald-400">
                ${status.summary.totalCommissionVolume.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Companies List */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Companies</h3>

            {status.companies.length === 0 ? (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700/50 text-center">
                <Database className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No companies found</p>
              </div>
            ) : (
              status.companies.map((company) => (
                <div
                  key={company.companyId}
                  className={`bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border-l-4 border border-slate-700/50 ${getStatusColor(company.status)}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{company.companyName}</h4>
                      <p className="text-sm text-slate-400 font-mono">{company.companyId}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-lg text-sm font-medium border ${getStatusBadgeColor(company.status)}`}>
                      {company.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Aggregate Documents</p>
                      <p className="text-2xl font-bold text-white">{company.aggregateCount}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Total Commission</p>
                      <p className="text-2xl font-bold text-white">
                        ${Math.round(company.totalCommission / 100).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Has Commission Data</p>
                      <p className="text-2xl font-bold text-white">
                        {company.hasCommissionData ? '✅ Yes' : '❌ No'}
                      </p>
                    </div>
                  </div>

                  {company.months.length > 0 && (
                    <div className="pt-4 border-t border-slate-700/50">
                      <p className="text-xs text-slate-400 mb-2">Months with aggregates:</p>
                      <div className="flex flex-wrap gap-2">
                        {company.months.map((month) => (
                          <span
                            key={month}
                            className="px-3 py-1 bg-slate-700/50 rounded-lg text-sm text-slate-300 font-mono"
                          >
                            {month}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Info Box */}
          {status.summary.companiesNeedingRebuild > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-orange-400 font-semibold mb-2">Action Required</h3>
                  <p className="text-orange-300 text-sm mb-3">
                    {status.summary.companiesNeedingRebuild} {status.summary.companiesNeedingRebuild === 1 ? 'company has' : 'companies have'} commission data but no aggregates.
                    These companies need their aggregates rebuilt to show commission metrics in Analytics.
                  </p>
                  <p className="text-orange-300 text-sm">
                    <strong>Next steps:</strong> Contact the development team to trigger an aggregate rebuild for these companies.
                  </p>
                </div>
              </div>
            </div>
          )}

          {status.summary.companiesWithAggregates === status.summary.totalCompanies && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-green-400 font-semibold mb-2">All Systems Operational</h3>
                  <p className="text-green-300 text-sm">
                    All companies with commission data have monthly aggregates! Commission metrics in the Analytics dashboard are fully functional.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Comparison Results */}
      {comparison && (
        <div className="space-y-6 mt-8 pt-8 border-t border-slate-700">
          <h2 className="text-2xl font-bold text-white">Accuracy Comparison Results</h2>

          {/* Summary */}
          <div className={`rounded-xl p-6 border ${
            comparison.summary.platformDifference < 0.10
              ? 'bg-green-500/10 border-green-500/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-start gap-3">
              {comparison.summary.platformDifference < 0.10 ? (
                <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <h3 className={`font-semibold mb-3 ${
                  comparison.summary.platformDifference < 0.10 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {comparison.diagnosis}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Source (commission_data)</p>
                    <p className="text-2xl font-bold text-white">${comparison.summary.platformSourceTotal.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Aggregates (monthly_aggregates)</p>
                    <p className="text-2xl font-bold text-white">${comparison.summary.platformAggregateTotal.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Difference</p>
                    <p className="text-2xl font-bold text-orange-400">${comparison.summary.platformDifference.toLocaleString()}</p>
                    <p className="text-sm text-slate-400">({comparison.summary.percentageDifference}%)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Per-Company Results */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Per-Company Breakdown</h3>
            {comparison.companies.map((company) => (
              <div
                key={company.companyId}
                className={`rounded-xl p-6 border ${
                  company.status === '✅ Matches'
                    ? 'bg-slate-800/50 border-green-500/30'
                    : 'bg-slate-800/50 border-red-500/30'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">{company.companyName}</h4>
                    <p className="text-sm text-slate-400 font-mono">{company.companyId}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    company.status === '✅ Matches'
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {company.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <h5 className="text-xs text-slate-400 mb-2">Source Data</h5>
                    <p className="text-lg font-bold text-white">${company.sourceData.total.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">{company.sourceData.recordCount} records</p>
                    <p className="text-xs text-slate-400">Upfront: ${company.sourceData.upfront.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">Trail: ${company.sourceData.trail.toLocaleString()}</p>
                  </div>

                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <h5 className="text-xs text-slate-400 mb-2">Aggregates</h5>
                    <p className="text-lg font-bold text-white">${company.aggregates.total.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">{company.aggregates.recordCount} records</p>
                    <p className="text-xs text-slate-400">Upfront: ${company.aggregates.upfront.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">Trail: ${company.aggregates.trail.toLocaleString()}</p>
                  </div>

                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <h5 className="text-xs text-slate-400 mb-2">Differences</h5>
                    <p className="text-lg font-bold text-orange-400">${company.differences.total.toLocaleString()}</p>
                    <p className="text-xs text-slate-400 mt-1">{company.differences.recordCount} records</p>
                    <p className="text-xs text-slate-400">Upfront: ${company.differences.upfront.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">Trail: ${company.differences.trail.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Types Analysis Results */}
      {paymentTypes && (
        <div className="space-y-6 mt-8 pt-8 border-t border-slate-700">
          <h2 className="text-2xl font-bold text-white">Payment Types Analysis</h2>

          {/* Summary */}
          <div className={`rounded-xl p-6 border ${
            paymentTypes.summary.uncountedTotal > 0
              ? 'bg-red-500/10 border-red-500/30'
              : 'bg-green-500/10 border-green-500/30'
          }`}>
            <div className="flex items-start gap-3">
              {paymentTypes.summary.uncountedTotal > 0 ? (
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <h3 className={`font-semibold mb-3 ${
                  paymentTypes.summary.uncountedTotal > 0 ? 'text-red-400' : 'text-green-400'
                }`}>
                  {paymentTypes.summary.uncountedTotal > 0
                    ? `Found ${paymentTypes.summary.uncountedTypes} uncounted payment type(s)`
                    : 'All payment types are being counted correctly'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Total Payment Types</p>
                    <p className="text-2xl font-bold text-white">{paymentTypes.summary.totalUniquePaymentTypes}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Counted in Aggregates</p>
                    <p className="text-2xl font-bold text-green-400">{paymentTypes.summary.countedTypes}</p>
                    <p className="text-sm text-slate-400">${paymentTypes.summary.countedTotal.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">NOT Counted</p>
                    <p className="text-2xl font-bold text-red-400">{paymentTypes.summary.uncountedTypes}</p>
                    <p className="text-sm text-slate-400">${paymentTypes.summary.uncountedTotal.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Missing Records</p>
                    <p className="text-2xl font-bold text-orange-400">{paymentTypes.summary.uncountedRecordCount.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Uncounted Payment Types */}
          {paymentTypes.uncountedTypes.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white">Uncounted Payment Types (Causing Missing $)</h3>
              {paymentTypes.uncountedTypes.map((type) => (
                <div
                  key={type.paymentType}
                  className="rounded-xl p-6 bg-slate-800/50 border border-red-500/30"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white mb-1 font-mono">{type.paymentType}</h4>
                      <p className="text-sm text-slate-400">Appears in: {type.appearsInCompanies.join(', ')}</p>
                    </div>
                    <span className="px-3 py-1 rounded-lg text-sm font-medium bg-red-500/20 text-red-400 border border-red-500/30">
                      NOT COUNTED
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h5 className="text-xs text-slate-400 mb-2">Total Amount</h5>
                      <p className="text-xl font-bold text-red-400">${type.totalAmount.toLocaleString()}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h5 className="text-xs text-slate-400 mb-2">Record Count</h5>
                      <p className="text-xl font-bold text-white">{type.recordCount.toLocaleString()}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <h5 className="text-xs text-slate-400 mb-2">Classification</h5>
                      <p className="text-xs text-slate-400">Upfront: {type.isUpfront ? '✅' : '❌'}</p>
                      <p className="text-xs text-slate-400">Trail: {type.isTrail ? '✅' : '❌'}</p>
                      <p className="text-xs text-slate-400">Counted: {type.isCounted ? '✅' : '❌'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!status && !loading && !error && !comparison && !paymentTypes && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <Database className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Ready to Check Status</h3>
          <p className="text-slate-400 mb-6">Click "Check Status" to scan all companies and analyze their monthly aggregates.</p>
        </div>
      )}
    </div>
  );
};
